import { Provider as JotaiProvider, useAtomValue, useSetAtom } from 'jotai';
import { ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { iqTheme, ThemeProvider } from '@statsbomb/kitbag-themes';
import { AppState, AuthProvider, useKitbagAuth } from '@statsbomb/kitbag-auth';
import { useLocation, useNavigate } from 'react-router-dom';
import env from '@/env';
import { useTracking } from '@/hooks/useTracking';
import { QueryClientProvider } from '@tanstack/react-query';
import { getFetchClient, getQueryClient } from '@/query/client';
import { useErrorMonitoring } from '@/hooks/useErrorMonitoring';
import { fetchClientAtom } from '@/atoms/queries/client';
import { DevTools, useAtomsDevtools } from 'jotai-devtools';
import { useHydrateAtoms } from 'jotai/utils';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClientAtom } from 'jotai-tanstack-query';
import { getSnowplowTracker } from '@hudl/analytics';
import Tracker from '@hudl/analytics/dist/snowplow/trackers/Tracker';
import 'jotai-devtools/styles.css';
import LogRocket from 'logrocket';
import { loadableUserDetailsAtom } from '@/atoms/queries/user';
import { identCookieAtom } from '@/atoms/cookies';
import { VideoProvider } from './VideoProvider';

const FetchProvider = ({ children }: { children: ReactNode }) => {
  const { getAccessTokenSilently } = useKitbagAuth();
  const setFetchClient = useSetAtom(fetchClientAtom);
  const [token, setToken] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const searchParams = window.location.search;
  const isHudlLoginAttempted = searchParams.includes('success=');
  const isDodgyEncoding = searchParams.includes('&amp;');
  const identCookieValue = useAtomValue(identCookieAtom);

  const navigate = useNavigate();

  useEffect(() => {
    const accessToken = getAccessTokenSilently();
    setFetchClient({ fetch: getFetchClient(accessToken) });
    accessToken.then(token => setToken(token));
  }, [getAccessTokenSilently, setFetchClient]);

  useEffect(() => {
    if (Number(env.VITE_ENABLE_HUDL_LOGIN) && token && formRef?.current && !isHudlLoginAttempted && !identCookieValue)
      formRef.current.requestSubmit();
  }, [token, formRef, isHudlLoginAttempted, identCookieValue]);

  // This shouldn't really be necessary, but the redirect we get from calling configure_cookie
  // has the search params encoded as &amp; instead of &, which causes issues with react-router.
  // This effect will replace &amp; with & in the URL if it detects it.
  useEffect(() => {
    if (isDodgyEncoding) {
      const { pathname, hash } = window.location;
      const newSearch = window.location.search.replace(/&amp;/g, '&');
      navigate(pathname + newSearch + hash, { replace: true });
    }
  }, [isDodgyEncoding]);

  return (
    <>
      {!!Number(env.VITE_ENABLE_HUDL_LOGIN) && (
        <form ref={formRef} method="post" className="hidden" action={`${env.VITE_HUDL_URL}/app/users/configure_cookie`}>
          <input name="Token" type="hidden" value={token} />
          <input name="Forward" type="hidden" value={window.location.href} />
        </form>
      )}
      {children}
    </>
  );
};

const SnowplowProvider = ({ children }: { children: ReactNode }) => {
  useTracking();
  const location = useLocation();

  // Configuring the snowplow tracker above is an async operation, but I don't want to make this component async
  // so we just wait for the tracker to be ready before we try to use it.
  let parachuteSnowplowTracker: Tracker | undefined;
  try {
    parachuteSnowplowTracker = getSnowplowTracker('parachute');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Error getting Snowplow tracker. Possibly not ready yet.', e);
  }

  // Track a unique page view event on every change to page or query params
  useEffect(() => {
    parachuteSnowplowTracker?.trackPageViewEvent(document.title);
  }, [parachuteSnowplowTracker, location.pathname, location.search]);

  // Track time on page for every unique page only
  useEffect(() => {
    parachuteSnowplowTracker?.trackPagePings(1, 1); // 1s delay, 1s interval
  }, [parachuteSnowplowTracker, location.pathname]);

  return children;
};

// prevent snowplow from trying to track the dev environment
const TrackingProvider = ({ children }: { children: ReactNode }) =>
  env.VITE_NODE_ENV !== 'development' ? <SnowplowProvider>{children}</SnowplowProvider> : children;

const LogRocketProvider = ({ children }: { children: ReactNode }) => {
  LogRocket.init('erd/sb-iq', { rootHostname: env.VITE_LOGROCKET_ROOT_HOSTNAME });
  return children;
};

const SessionReplayProvider = ({ children }: { children: ReactNode }) =>
  Number(env.VITE_ENABLE_LOGROCKET) ? <LogRocketProvider>{children}</LogRocketProvider> : children;

const LogRocketIdentityProvider = ({ children }: { children: ReactNode }) => {
  const userDetails = useAtomValue(loadableUserDetailsAtom);

  const { email, name } = userDetails?.state === 'hasData' ? userDetails.data.data.user : { email: '', name: '' };

  useEffect(() => {
    if (email) LogRocket.identify(email, { name: name || '', email });
  }, [email]);
  return children;
};

const SessionReplayIdentityProvider = ({ children }: { children: ReactNode }) =>
  Number(env.VITE_ENABLE_LOGROCKET) ? <LogRocketIdentityProvider>{children}</LogRocketIdentityProvider> : children;

const queryClient = getQueryClient();

const InitJotaiQueryClient = ({ children }: { children: React.ReactNode }) => {
  // Hydration for Jotai does not care if it's server to browser/client, it is for initialising atoms.
  // https://jotai.org/docs/extensions/query#referencing-the-same-instance-of-query-client-in-your-project
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  return children;
};

const AuthedClientProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    LogRocket.startNewSession();
    navigate((appState && appState.returnTo) || window.location.pathname);
  };

  return (
    <AuthProvider
      providerOptions={{
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: env.VITE_AUTH0_AUDIENCE,
        },
        clientId: env.VITE_AUTH0_CLIENT_ID,
        domain: env.VITE_AUTH0_DOMAIN,
        onRedirectCallback,
      }}
    >
      <JotaiProvider>
        <InitJotaiQueryClient>
          <VideoProvider>
            <Suspense>
              <FetchProvider>
                <SessionReplayIdentityProvider>
                  <TrackingProvider>{children}</TrackingProvider>
                </SessionReplayIdentityProvider>
              </FetchProvider>
            </Suspense>
          </VideoProvider>
        </InitJotaiQueryClient>
      </JotaiProvider>
    </AuthProvider>
  );
};

// We needed to separate the theme provider so we could pass in the Jotai state
// Jotai state must be defined below the provider or it doesn't work
export const KitbagThemeProvider = ({ children }: { children: ReactNode }) => (
  // Hardcoding the theme to light for now, as we've removed the ability to change it in the UI.
  <ThemeProvider theme={iqTheme.light}>{children}</ThemeProvider>
);

export const Providers = ({ children }: { children: ReactNode }) => {
  useErrorMonitoring();
  useAtomsDevtools('parachute-ui');
  return (
    <SessionReplayProvider>
      <QueryClientProvider client={queryClient}>
        <AuthedClientProvider>
          <DevTools />
          <KitbagThemeProvider>{children}</KitbagThemeProvider>
        </AuthedClientProvider>
        {env.VITE_ENABLE_TANSTACK_DEVTOOLS === '1' && <ReactQueryDevtools position="bottom" />}
      </QueryClientProvider>
    </SessionReplayProvider>
  );
};
